import React, { useState, useEffect } from 'react';
import { 
  Activity, TrendingUp, Target, AlertTriangle, 
  CheckCircle, Clock, Flame, Calendar, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { getApiBaseUrl } from '../services/api';

const API_URL = getApiBaseUrl();

export default function PrepOSStudentWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token') || '';

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prep-os/dashboard-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch PrepOS stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/80 border-slate-200 animate-pulse">
        <CardContent className="p-6">
          <div className="h-32 bg-slate-200 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const { metrics, risk_status, today_compliance, weekly_study_hours } = stats;

  const getRiskColor = (level) => {
    switch(level) {
      case 'RED': return 'bg-red-500 text-white';
      case 'YELLOW': return 'bg-yellow-500 text-white';
      case 'GREEN': return 'bg-green-500 text-white';
      default: return 'bg-slate-500 text-white';
    }
  };

  const getRiskMessage = (level) => {
    switch(level) {
      case 'RED': return '⚠️ You need to improve your consistency!';
      case 'YELLOW': return '💪 Good effort! Push a bit harder.';
      case 'GREEN': return '🎯 Great job! Keep it up!';
      default: return '';
    }
  };

  return (
    <div className="space-y-4" data-testid="prep-os-student-widget">
      {/* Risk Status Alert */}
      {risk_status && risk_status.risk_level !== 'GREEN' && (
        <Card className={`border-2 ${risk_status.risk_level === 'RED' ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'}`} data-testid="risk-status-alert">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`h-5 w-5 mt-0.5 ${risk_status.risk_level === 'RED' ? 'text-red-500' : 'text-yellow-500'}`} />
              <div>
                <p className={`font-medium ${risk_status.risk_level === 'RED' ? 'text-red-700' : 'text-yellow-700'}`}>
                  {getRiskMessage(risk_status.risk_level)}
                </p>
                <ul className="text-sm mt-1 space-y-1">
                  {risk_status.reasons.map((reason, i) => (
                    <li key={i} className="text-slate-600">• {reason}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="metrics-grid">
        {/* Consistency Score */}
        <Card className="bg-white/80 border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-blue-500" />
              <span className="text-xs text-slate-500">Consistency</span>
            </div>
            <p className="text-2xl font-bold text-blue-600" data-testid="consistency-score">{metrics.consistency_score}%</p>
            <Progress value={metrics.consistency_score} className="h-1 mt-2" />
          </CardContent>
        </Card>

        {/* Study Intensity */}
        <Card className="bg-white/80 border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              <span className="text-xs text-slate-500">Intensity</span>
            </div>
            <p className="text-2xl font-bold text-green-600" data-testid="study-intensity">{metrics.study_intensity}%</p>
            <Progress value={Math.min(metrics.study_intensity, 100)} className="h-1 mt-2" />
          </CardContent>
        </Card>

        {/* Streak */}
        <Card className="bg-white/80 border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Flame className="h-4 w-4 text-orange-500" />
              <span className="text-xs text-slate-500">Streak</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{metrics.streak_days}</p>
            <p className="text-xs text-slate-500">days</p>
          </CardContent>
        </Card>

        {/* Study Hours (7d) */}
        <Card className="bg-white/80 border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-purple-500" />
              <span className="text-xs text-slate-500">This Week</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{metrics.total_study_hours_7d}</p>
            <p className="text-xs text-slate-500">hours</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Compliance */}
      {today_compliance && (
        <Card className="bg-white/80 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4 text-indigo-500" />
              Today&apos;s Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-3">
              <Progress value={today_compliance.completion_percent} className="flex-1 h-2" />
              <span className="text-sm font-medium text-slate-700">
                {today_compliance.completion_percent}%
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className={`p-2 rounded ${today_compliance.completed_concept ? 'bg-green-100' : 'bg-slate-100'}`}>
                <CheckCircle className={`h-4 w-4 mx-auto ${today_compliance.completed_concept ? 'text-green-500' : 'text-slate-400'}`} />
                <p className="text-xs mt-1">Concept</p>
              </div>
              <div className={`p-2 rounded ${today_compliance.completed_editorial ? 'bg-green-100' : 'bg-slate-100'}`}>
                <CheckCircle className={`h-4 w-4 mx-auto ${today_compliance.completed_editorial ? 'text-green-500' : 'text-slate-400'}`} />
                <p className="text-xs mt-1">Editorial</p>
              </div>
              <div className={`p-2 rounded ${today_compliance.completed_test ? 'bg-green-100' : 'bg-slate-100'}`}>
                <CheckCircle className={`h-4 w-4 mx-auto ${today_compliance.completed_test ? 'text-green-500' : 'text-slate-400'}`} />
                <p className="text-xs mt-1">Test</p>
              </div>
              <div className={`p-2 rounded ${today_compliance.completed_revision ? 'bg-green-100' : 'bg-slate-100'}`}>
                <CheckCircle className={`h-4 w-4 mx-auto ${today_compliance.completed_revision ? 'text-green-500' : 'text-slate-400'}`} />
                <p className="text-xs mt-1">Revision</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Weekly Study Chart */}
      {weekly_study_hours && weekly_study_hours.length > 0 && (
        <Card className="bg-white/80 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-cyan-500" />
              Weekly Study Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between h-24 gap-1">
              {weekly_study_hours.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-cyan-500 rounded-t"
                    style={{ 
                      height: `${Math.max((day.hours / 5) * 100, 5)}%`,
                      minHeight: day.hours > 0 ? '8px' : '2px'
                    }}
                  ></div>
                  <p className="text-xs text-slate-500 mt-1">{day.day}</p>
                  <p className="text-xs font-medium text-slate-700">{day.hours}h</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
